import { ReactNode, useState } from 'react';
import styled from 'styled-components';

import { Button, IconSettings as SettingsIcon, IconX as XIcon } from '../../core';

interface Props {
  className?: string;
  iconName: 'gear';
  mainText: string;
  numberOfAlerts?: number;
  additionalText?: string;
  actions?: ReactNode;
}

const icons = {
  gear: <SettingsIcon className="gear-icon" />,
};

const Alert = ({
  className = '',
  iconName,
  mainText,
  numberOfAlerts = 0,
  additionalText = '',
  actions = null,
}: Props) => {
  const [isOpen, setIsOpen] = useState(true);

  if (!isOpen) {
    return null;
  }

  return (
    <div role="alert" className={className}>
      <div className="alert-content">
        <p className="alert-title">
          {icons[iconName]}
          {mainText}
        </p>

        <div className="alert-body">
          {numberOfAlerts > 0 && <span className="number-of-alerts">{numberOfAlerts} new</span>}
          {additionalText && <p className="additional-text">{additionalText}</p>}
        </div>

        <div className="actions">{actions}</div>
      </div>
      <Button
        onClick={() => setIsOpen(false)}
        size="XS"
        iconOnly
        icon={<XIcon />}
        aria-label="Close alert"
        className="close-btn"
      />
    </div>
  );
};

const StyledAlert = styled(Alert)`
  margin-left: 16px;
  margin-right: 16px;
  padding: 12px;
  border-radius: 6px;
  background: linear-gradient(
      to left,
      ${({ theme }) => theme.neutralBackgroundBase},
      ${({ theme }) => theme.neutralBackgroundTransparent}
    ),
    ${({ theme }) => theme.primaryBackground};
  border: 1px solid rgba(38, 133, 161, 0.24); /* tokens-allow: pending design decision */
  display: flex;
  align-items: start;
  justify-content: space-between;
  position: relative;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    align-items: center;
    padding: 10px 16px;
  }

  > .alert-content {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 8px;

    > .alert-title {
      color: ${({ theme }) => theme.primaryText};
      font-size: ${({ theme }) => theme.fontSizes.base};
      line-height: ${({ theme }) => theme.lineHeights.base};
      font-weight: 600;

      > .gear-icon {
        color: ${({ theme }) => theme.primaryText};
        margin-right: 8px;
        position: relative;
        top: 2px;
      }
    }

    > .alert-body {
      display: flex;
      align-items: start;
      gap: 8px;
      > .additional-text {
        color: ${({ theme }) => theme.neutralTextWeak};
        font-size: ${({ theme }) => theme.fontSizes.base};
        line-height: ${({ theme }) => theme.lineHeights.base};
        font-weight: 400;
        max-width: 450px;

        @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
          max-width: 100%;
        }
      }

      > .number-of-alerts {
        white-space: nowrap;
        background-color: ${({ theme }) => theme.primaryBackgroundStrong};
        color: ${({ theme }) => theme.primaryTextStrong};
        padding: 3px 4px;
        font-size: ${({ theme }) => theme.fontSizes.xs};
        line-height: ${({ theme }) => theme.lineHeights.xs};
        font-weight: 400;
        border-radius: 2px;
      }
    }

    > .actions {
      display: none;
      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        margin-left: 24px;
        display: flex;
        align-items: center;
      }
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      flex-direction: row;
    }
  }

  /* The box, size, colours and states come from the core Button (default type, XS); only the
     placement is set here. */
  .close-btn {
    position: absolute;
    top: 4px;
    right: 4px;

    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      position: unset;
    }
  }
`;

export default StyledAlert;
